import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu, Database } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { UserMenu } from "./UserMenu";
import { PageHeaderProvider, usePageHeader } from "./PageHeaderContext";
import { CommandPalette } from "@/components/CommandPalette";
import { apiFetch } from "@/lib/api";

/**
 * Isolated context consumer — only this component re-renders when the page
 * header changes. It never renders <Outlet /> so there's no cascade back
 * into the page that called useSetPageHeader().
 */
function PageHeaderSlot() {
  const header = usePageHeader();
  if (!header) return <div className="flex-1" />;

  const Icon = header.icon;
  return (
    <div className="flex flex-1 items-center gap-2.5 min-w-0">
      {Icon && (
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground hidden sm:block" />
      )}
      <div className="min-w-0 mr-auto">
        <p className="truncate text-sm font-semibold leading-tight">
          {header.title}
        </p>
        {header.subtitle && (
          <p className="truncate text-xs text-muted-foreground leading-tight hidden md:block">
            {header.subtitle}
          </p>
        )}
      </div>
      {header.actions && (
        <div className="flex items-center gap-2 shrink-0">
          {header.actions}
        </div>
      )}
    </div>
  );
}

function DataFreshness() {
  const { data } = useQuery({
    queryKey: ["data-freshness"],
    queryFn: () => apiFetch<{ latest: string | null }>("/api/production/freshness"),
    staleTime: 5 * 60 * 1000,
  });

  if (!data?.latest) return null;

  const formatted = new Date(data.latest).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <span className="hidden items-center gap-1 text-[10px] text-muted-foreground/60 sm:flex" title="Latest production data timestamp">
      <Database className="h-3 w-3" />
      {formatted}
    </span>
  );
}

function AppShellInner() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-14 items-center gap-3 border-b border-border px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-muted-foreground hover:text-foreground"
          >
            <Menu className="h-5 w-5" />
          </button>

          <PageHeaderSlot />

          <DataFreshness />
          <UserMenu />
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      <CommandPalette />
    </div>
  );
}

export function AppShell() {
  return (
    <PageHeaderProvider>
      <AppShellInner />
    </PageHeaderProvider>
  );
}
